/* LT-AO-01 — A&O Library edit / create at /ao-library/{id} or /ao-library/new */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton } from "../components/UI";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";

const cls = (...a) => a.filter(Boolean).join(" ");

const TYPES = [
  ["assumption",    "Assumption"],
  ["clarification", "Clarification"],
  ["omission",      "Omission"],
];

function defaultDoc(type = "assumption") {
  return {
    type,
    statement_text: "",
    applies_to_job_types: [],
    display_order: 99,
    is_active: true,
  };
}

export default function AOLibraryEdit() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { activeTypes: jobTypes } = useJobTypeLibrary();
  const isNew = !id || id === "new";
  const [doc, setDoc] = useState(defaultDoc(search.get("type") || "assumption"));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/ao-library/${id}`)
      .then((r) => setDoc(r.data))
      .catch(() => { toast.error("Failed to load item"); navigate("/ao-library"); })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));

  const toggleTag = (k) => {
    setDoc((d) => {
      const tags = d.applies_to_job_types || [];
      if (k === "all") {
        return { ...d, applies_to_job_types: tags.includes("all") ? [] : ["all"] };
      }
      const without = tags.filter((x) => x !== "all");
      return {
        ...d,
        applies_to_job_types: without.includes(k)
          ? without.filter((x) => x !== k)
          : [...without, k],
      };
    });
  };

  const save = async () => {
    if (!doc.statement_text.trim()) {
      toast.error("Statement text is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/ao-library", doc);
        toast.success("Statement created");
        navigate(`/ao-library/${r.data.id}`);
      } else {
        const r = await api.put(`/ao-library/${id}`, doc);
        setDoc(r.data);
        toast.success("Saved");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this statement? Existing tenders are unaffected.")) return;
    try {
      await api.delete(`/ao-library/${id}`);
      toast.success("Archived");
      navigate("/ao-library");
    } catch (e) {
      console.error(e);
      toast.error("Archive failed");
    }
  };

  if (loading) {
    return <div className="app-container py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;
  }

  const tags = doc.applies_to_job_types || [];

  return (
    <div className="app-container py-8">
      <button
        onClick={() => navigate("/ao-library")}
        className="inline-flex items-center gap-1 text-ink-3 hover:text-ink mb-3"
        style={{ fontSize: 12 }}
      >
        <ArrowLeft size={12} /> Back to A&O Library
      </button>
      <PageHeader
        title={isNew ? "New A&O statement" : "Edit A&O statement"}
        subtitle={isNew ? "Defines a master statement that tenders can seed from." : "Edits don't affect existing tenders — only newly-seeded ones."}
        actions={
          <div className="flex items-center gap-2">
            {!isNew && (
              <button onClick={archive} className="inline-flex items-center gap-1 h-9 px-3 text-note-red hover:bg-note-red/5 rounded-md" style={{ fontSize: 12 }} data-testid="ao-library-edit-archive">
                <Trash2 size={12} /> Archive
              </button>
            )}
            <PrimaryButton icon={Save} onClick={save} disabled={saving} data-testid="ao-library-edit-save">
              {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </PrimaryButton>
          </div>
        }
      />

      <Card>
        <div className="p-5 grid grid-cols-2 gap-5">
          <div className="col-span-2">
            <label className="col-head text-ink-3 block mb-1">Type</label>
            <div className="inline-flex p-0.5 bg-surface-2 rounded-md border border-line">
              {TYPES.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set({ type: k })}
                  data-testid={`ao-library-edit-type-${k}`}
                  disabled={!isNew}
                  className={cls(
                    "px-3 h-8 rounded transition-colors",
                    doc.type === k
                      ? "bg-surface text-accent shadow-sm"
                      : "text-ink-3 hover:text-ink-2",
                    !isNew && "cursor-not-allowed",
                  )}
                  style={{ fontSize: 12, fontWeight: doc.type === k ? 600 : 500 }}
                >
                  {l}
                </button>
              ))}
            </div>
            {!isNew && <div className="text-ink-3 mt-1 italic" style={{ fontSize: 11 }}>Type is locked once created.</div>}
          </div>

          <div className="col-span-2">
            <label className="col-head text-ink-3 block mb-1">Statement text</label>
            <textarea
              value={doc.statement_text}
              onChange={(e) => set({ statement_text: e.target.value })}
              spellCheck="true"
              data-testid="ao-library-edit-text"
              className="w-full p-3 rounded-md border border-line bg-surface text-ink resize-vertical focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13, lineHeight: "1.5", minHeight: 100 }}
            />
          </div>

          <div className="col-span-2">
            <label className="col-head text-ink-3 block mb-2">Applies to job types</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => toggleTag("all")}
                data-testid="ao-library-edit-tag-all"
                className={cls(
                  "px-3 h-8 rounded-md border transition-colors",
                  tags.includes("all")
                    ? "bg-accent text-white border-accent"
                    : "border-line bg-surface text-ink-2 hover:bg-surface-2",
                )}
                style={{ fontSize: 12 }}
              >All job types</button>
              {jobTypes.map((jt) => (
                <button key={jt.key}
                  type="button"
                  onClick={() => toggleTag(jt.key)}
                  data-testid={`ao-library-edit-tag-${jt.key}`}
                  disabled={tags.includes("all")}
                  className={cls(
                    "px-3 h-8 rounded-md border transition-colors",
                    tags.includes(jt.key) && !tags.includes("all")
                      ? "bg-accent text-white border-accent"
                      : "border-line bg-surface text-ink-2 hover:bg-surface-2",
                    tags.includes("all") && "opacity-40 cursor-not-allowed",
                  )}
                  style={{ fontSize: 12 }}
                >{jt.label}</button>
              ))}
            </div>
            <div className="text-ink-3 mt-2 italic" style={{ fontSize: 11 }}>
              Empty selection = item lives in the library but won't auto-tick on any job type.
            </div>
          </div>

          <div>
            <label className="col-head text-ink-3 block mb-1">Display order</label>
            <input
              type="number" step="1"
              value={doc.display_order ?? 0}
              onChange={(e) => set({ display_order: Number(e.target.value) })}
              data-testid="ao-library-edit-order"
              className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
          <div>
            <label className="col-head text-ink-3 block mb-1">Active</label>
            <label className="inline-flex items-center gap-2 h-10" style={{ fontSize: 13 }}>
              <input
                type="checkbox"
                checked={doc.is_active !== false}
                onChange={(e) => set({ is_active: e.target.checked })}
                data-testid="ao-library-edit-active"
              />
              <span className="text-ink-2">Show in tender seeding</span>
            </label>
          </div>
        </div>
      </Card>
    </div>
  );
}
